// SPDX-License-Identifier: Apache-2.0

// tether-brain v2 — CPU TF-IDF index (v1 fallback search).
//
// Used until Prometheus's WGSL `tfidfSpMV` kernel ships. Persists vocab + raw
// postings into Dexie so a page reload can search without re-tokenizing the
// whole corpus.
//
// Algorithm: standard `(tf / Σtf) * log(N / df)` weighting, unit-normalized
// per doc, cosine for query-time scoring. Matches `vault-tfidf.ts` from the
// old repo — intentional, so search results stay stable across the migration.

import { getDB } from './dexie-schema';
import type { TfidfPosting, TfidfVocab, VaultDoc } from './schema';

// Minimal English stopword set, mirroring the old impl. Turkish stopwords are
// omitted on purpose — tokens like "ve" or "bir" survive but get aggressively
// punished by IDF in a Turkish-heavy corpus, so the impact is small. A full
// multilingual list lives in the future `@tether/ingest` lang module.
const STOP = new Set([
  'a','an','and','are','as','at','be','but','by','can','could','did','do','does',
  'for','from','had','has','have','he','her','him','his','how','i','if','in','is',
  'it','its','just','me','more','most','my','no','not','of','on','or','our','she',
  'should','so','some','such','than','that','the','their','them','then','there',
  'these','they','this','those','to','was','we','were','what','when','where',
  'which','who','why','will','with','would','you','your','also','been','being',
]);

export function tokenize(text: string): string[] {
  const clean = text
    .replace(/```[\s\S]*?```/g, ' ')
    .replace(/`[^`]+`/g, ' ')
    .replace(/\[\[([^\]]+)\]\]/g, '$1')
    .replace(/\[([^\]]*)\]\([^)]+\)/g, '$1')
    .toLowerCase();
  const out: string[] = [];
  for (const p of clean.split(/[^a-z0-9çğıöşü]+/)) {
    if (p.length < 3) continue;
    if (STOP.has(p)) continue;
    if (/^\d+$/.test(p)) continue;
    out.push(p);
  }
  return out;
}

// ─── build ──────────────────────────────────────────────────────────────────

export interface BuildTfidfOptions {
  /** Throw away terms with df below this threshold — keeps the index lean
   *  on small vaults. Default 1 (keep everything). */
  minDf?: number;
}

/** Wipe and rebuild the persisted TF-IDF index from `docs`. O(total_tokens). */
export async function buildTfidfIndex(
  docs: VaultDoc[],
  opts: BuildTfidfOptions = {},
): Promise<void> {
  const minDf = opts.minDf ?? 1;
  const N = docs.length;
  const db = getDB();

  // Stage 1 — TF per doc.
  const tfs = new Map<string, Map<string, number>>();
  for (const d of docs) {
    const tf = new Map<string, number>();
    for (const t of tokenize(d.content)) tf.set(t, (tf.get(t) ?? 0) + 1);
    tfs.set(d.id, tf);
  }

  // Stage 2 — DF.
  const df = new Map<string, number>();
  for (const tf of tfs.values()) {
    for (const term of tf.keys()) df.set(term, (df.get(term) ?? 0) + 1);
  }

  // Stage 3 — emit vocab + postings.
  const vocab: TfidfVocab[] = [];
  for (const [term, d] of df) {
    if (d < minDf) continue;
    vocab.push({ term, df: d, idf: Math.log((N + 1) / (d + 1)) + 1 });
  }
  const postings: TfidfPosting[] = [];
  for (const [docId, tf] of tfs) {
    for (const [term, count] of tf) {
      if ((df.get(term) ?? 0) < minDf) continue;
      postings.push({
        id: `${term}::${docId}`,
        term,
        docId,
        tf: count,
      });
    }
  }

  await db.transaction(
    'rw',
    db.tfidf_vocab,
    db.tfidf_postings,
    async () => {
      await db.tfidf_vocab.clear();
      await db.tfidf_postings.clear();
      if (vocab.length > 0) await db.tfidf_vocab.bulkPut(vocab);
      if (postings.length > 0) await db.tfidf_postings.bulkPut(postings);
    },
  );
}

// ─── query ──────────────────────────────────────────────────────────────────

export interface TfidfHit {
  docId: string;
  /** Cosine similarity in [0, 1]. */
  score: number;
}

/** Score a query against the persisted TF-IDF index. Pulls only the postings
 *  matching the query terms — does NOT scan the full corpus, so this stays
 *  fast even for 10k-doc vaults. */
export async function queryTfidf(
  query: string,
  k: number = 10,
): Promise<TfidfHit[]> {
  const terms = tokenize(query);
  if (terms.length === 0) return [];
  const uniq = Array.from(new Set(terms));
  const db = getDB();

  // Lookup vocab to get IDF + filter unknown terms.
  const vocabRows = await db.tfidf_vocab.where('term').anyOf(uniq).toArray();
  const idf = new Map(vocabRows.map((v) => [v.term, v.idf]));
  if (idf.size === 0) return [];

  // Build query vector (TF for the query, weighted by IDF).
  const qTf = new Map<string, number>();
  for (const t of terms) {
    if (!idf.has(t)) continue;
    qTf.set(t, (qTf.get(t) ?? 0) + 1);
  }
  let qNorm2 = 0;
  const qWeights = new Map<string, number>();
  for (const [t, c] of qTf) {
    const w = c * (idf.get(t) ?? 0);
    qWeights.set(t, w);
    qNorm2 += w * w;
  }
  const qNorm = Math.sqrt(qNorm2) || 1;

  // Pull postings for all query terms in parallel, accumulate per-doc.
  const knownTerms = Array.from(qWeights.keys());
  const postings = await db.tfidf_postings
    .where('term')
    .anyOf(knownTerms)
    .toArray();

  // We need each doc's full TF norm for cosine, so fetch their full posting
  // sets too. Cheaper than persisting per-doc norms, since a query rarely
  // touches more than a few hundred docs.
  const docIds = Array.from(new Set(postings.map((p) => p.docId)));
  const allPostings = await db.tfidf_postings
    .where('docId')
    .anyOf(docIds)
    .toArray();
  const docNorm = new Map<string, number>();
  for (const p of allPostings) {
    const wIdf = idf.get(p.term);
    // If we don't have an IDF cached for this term, look it up — but most
    // terms in `allPostings` will already be in `idf` since they intersect.
    if (wIdf === undefined) continue;
    const w = p.tf * wIdf;
    docNorm.set(p.docId, (docNorm.get(p.docId) ?? 0) + w * w);
  }

  // Cosine.
  const dots = new Map<string, number>();
  for (const p of postings) {
    const w = (qWeights.get(p.term) ?? 0) * p.tf * (idf.get(p.term) ?? 0);
    if (w === 0) continue;
    dots.set(p.docId, (dots.get(p.docId) ?? 0) + w);
  }

  const hits: TfidfHit[] = [];
  for (const [docId, dot] of dots) {
    const norm = Math.sqrt(docNorm.get(docId) ?? 0) * qNorm;
    if (norm === 0) continue;
    hits.push({ docId, score: dot / norm });
  }
  hits.sort((a, b) => b.score - a.score);
  return hits.slice(0, k);
}

/** Drop the entire persisted index — used by `VaultStore.removeDoc` when it's
 *  cheaper to wipe and rebuild than to surgically remove postings. */
export async function clearTfidfIndex(): Promise<void> {
  const db = getDB();
  await db.transaction(
    'rw',
    db.tfidf_vocab,
    db.tfidf_postings,
    async () => {
      await db.tfidf_vocab.clear();
      await db.tfidf_postings.clear();
    },
  );
}
